import React, { useContext } from 'react'
import { Trans, useTranslation } from 'react-i18next';
import { i18nContext } from '../i18n/i18nContext';
import { PageNames, PageContext } from '../Home/HomeWork';
import { HeldTimesContext } from '../Root/CompetitionRoot';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { Box, Button, Container, MenuItem, Select, TextField, Typography, FormControl, InputLabel, FormHelperText, TextFieldProps, FormControlLabel, Checkbox } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import { Title } from '../Component/Title';
import { Info } from '../Component/Info';

import { ErrorIndicator } from '../Utility/ErrorIndicator';
import { CountryDataType } from '../DataFetch/useCountryData';
import { MailDataType } from '../DataFetch/useMailData';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/ja'
import 'dayjs/locale/en' 
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween'; // eslint-disable-line
import { MuiFileInput } from 'mui-file-input';

export interface FormInputType{
    mailaddress: string,
    name:string,
    country:number,
    birthday:string,
    educational:string,
    educationalCountry:number,
    certificate:File | null,
    certificateBase64:string | undefined,
    group:boolean,
    members:MemberInputType[] | undefined,
    workFile:File | null,
    product:boolean
}

export interface MemberInputType{
    name:string,
    country:number,
    birthday:string,
    educational:string,
    educationalCountry:number,
    certificate:File | null,
    certificateBase64:string | undefined,
}

interface PropsType {
    MailData:MailDataType
    CountryData:CountryDataType[]
    formData:FormInputType | undefined
    setFormData:(inputData:FormInputType) => void
}

//ファイルサイズが3MB以下か確認する
function isFileSizeLessThan3MB (file:File | null) {

    if (file !== null) {
        return (file.size <= 3145728 ? true : false);
    }

    return false
}

//ファイル形式が正しいか確認する
function isCorrectFileType (file:File | null, fileTypes:string[]) {
    if (file !== null) {
        return (fileTypes.includes(file.type))
    }
    
    return false
}

/**
 * ファイルをBase64形式に変換する
 * @param File 変換するファイル
 * @returns Base64文字列
 */
function File2Base64(File:File) {
    const reader = new FileReader();
    return new Promise<string | ArrayBuffer | null | undefined>((resolve) => {
        reader.onload = (e) => {
            resolve(e.target?.result);
        }
        reader.readAsDataURL(File)
    })
}

/**
 * 作品登録入力フォーム
 */
export function KaguWorkForm({MailData, CountryData, formData, setFormData}:PropsType) {

    const [t] = useTranslation();

    const {lng} = useContext(i18nContext);

    const {setPage} = useContext(PageContext);

    const heldTimesOption = useContext(HeldTimesContext);
    
    const memberDefault:MemberInputType = {
        name:"",
        country:0,
        birthday:"",
        educational:"",
        educationalCountry:0,
        certificate:null,
        certificateBase64:""
    }

    const { watch, control, handleSubmit } = useForm<FormInputType>({
        defaultValues:{
            mailaddress:MailData.mailaddress,
            name:formData !== undefined ? formData.name : "",
            country: formData !== undefined ? formData.country : 0,
            birthday:formData !== undefined ? formData.birthday : "",
            educational:formData !== undefined ? formData.educational : "",
            educationalCountry:formData !== undefined ? formData.educationalCountry : 0,
            certificate: formData !== undefined ? formData.certificate : null,
            group:formData !== undefined ? formData.group : false,
            members:formData !== undefined ? formData.members : [memberDefault],
            workFile:formData !== undefined ? formData.workFile : null,
            product:formData !== undefined ? formData.product : false
        }
    });

    const {fields ,append, remove} = useFieldArray({
        control: control,
        name: "members"
    })

    const watchGroup = watch("group")

    /**
     * 国マスタドロップダウンリストオプション生成
     */
    function CountryOption() {
        return(
            CountryData?.map((item:CountryDataType) => (
                <MenuItem key={item.id} value={item.id} >
                    {item.dropdown_disp}
                </MenuItem>
            ))
        )
    }

    async function onSubmit(inputData:FormInputType) {

        inputData.mailaddress = MailData.mailaddress

        //グループ設定されていなければ、メンバー情報を空にする
        if(!inputData?.group) {
            inputData.members = []
        }

        var tempMembers: MemberInputType[] = []

        //代表者の在籍証明をBase64形式に変換
        await File2Base64(inputData.certificate!)
        .then(res => {
            return inputData.certificateBase64 = res?.toString();
        })
        .then(async () => {
            //グループメンバーの在籍証明ファイルをBase64に変換し、データに格納
            return inputData?.members?.map(async (value:MemberInputType, index:number) => {
                await File2Base64(value.certificate!)
                .then(res => {
                    value.certificateBase64 = res?.toString();
                    // tempMembers.push(value)
                    tempMembers[index] = value
                })        
            })
        })
        .then(() => {
            //グループメンバー情報をBase64変換後のデータに更新
            if (inputData.group) {
                inputData.members = tempMembers
            } else {
                //確認画面から戻った時にメンバー1が表示されなくなるので、デフォルトのメンバー情報を格納しておく
                inputData.members = [{...memberDefault}]
            }
            
            //登録情報をstateに保存
            setFormData(inputData)
            //確認ページに遷移
            setPage(PageNames.KaguWorkConfirm)
        })
    }

    return(
        <>
            <Box>
                <Title>
                    {t("KaguWorkForm.Title1", heldTimesOption)}
                </Title>
                <Info>
                    {t("KaguWorkForm.RegisterAlert1")}
                </Info>
            </Box>

            <Container maxWidth="sm">

                <Box m={2} pb={2}>
                    <form onSubmit={handleSubmit(onSubmit)} noValidate>
                        {/* メールアドレス(表示のみ) */}
                        <Box pb={1}>
                            <Controller
                                name="mailaddress"
                                control={control}
                                defaultValue={MailData.mailaddress}
                                render={({field}) => (
                                    <TextField
                                        {...field}
                                        label={t("KaguWorkForm.MailAddress")}
                                        variant="outlined" 
                                        fullWidth
                                        size="small"
                                        margin="normal"
                                        InputProps={{readOnly: true,}}
                                    />
                                )}
                            />
                        </Box>
                        
                        <Box pb={2}>
                            {/* 氏名 */}
                            <Box pb={1}>
                                <Controller
                                    name="name"
                                    control={control}
                                    defaultValue={""}
                                    rules={{required:true}}
                                    render={({field:{ref, ...field}, formState:{errors}}) => (
                                        <TextField
                                            inputRef={ref}
                                            {...field}
                                            label={t("KaguWorkForm.Name")}
                                            variant="outlined" 
                                            fullWidth
                                            size="small"
                                            margin="normal"
                                            error={errors.name ? true : false}
                                            helperText={ErrorIndicator(errors.name)}
                                        />
                                    )}
                                />
                            </Box>

                            {/* 国・地域 */}
                            <Box pb={2}>
                                <Controller
                                    name="country"
                                    control={control}
                                    rules={{
                                        validate: {required: (value) => {return value !== 0 }}
                                    }}
                                    render={({ field:{ref, ...field}, formState: { errors } }) => (
                                        <FormControl fullWidth size="small"
                                            error={errors.country ? true : false}>
                                            <InputLabel id="country-label"
                                                error={errors.country ? true : false}
                                            >
                                                {t("KaguWorkForm.Country")}
                                            </InputLabel>
                                            <Select
                                                labelId="country-label"
                                                id="country"
                                                label={t("KaguWorkForm.Country")}
                                                inputRef={ref}
                                                {...field}
                                                sx={{textAlign:'left'}}                                                
                                            >
                                                <MenuItem value={0}>{t("KaguWorkForm.CountryAnnotation")}</MenuItem>
                                                {CountryOption()}
                                            </Select>
                                            <FormHelperText error={errors.country ? true : false}>
                                                {ErrorIndicator(errors.country)}
                                            </FormHelperText>
                                        </FormControl>
                                    )}
                                />
                            </Box>

                            {/* 生年月日 */}
                            <Box pb={1}>
                                <Controller
                                    name="birthday"
                                    control={control}
                                    rules={{
                                        required:true,
                                        validate: {
                                            invalidDate: (value) => {return dayjs(value, "YYYY/MM/DD", true).isValid()},
                                            ageOver30: (value) => {return dayjs().diff(dayjs(value), "year") <= 30},                                              
                                        }
                                        
                                    }}
                                    render={({field:{ref, ...field},formState:{errors}}) => (
                                        <LocalizationProvider
                                            dateAdapter={AdapterDayjs}
                                            adapterLocale={lng}
                                        >
                                            <DesktopDatePicker
                                                inputRef={ref}
                                                {...field}
                                                // disableMaskedInput
                                                inputFormat={lng === "ja" ? "YYYY/MM/DD" : "MM/DD/YYYY"}
                                                maxDate={dayjs().format("YYYY/MM/DD")}
                                                value={field.value || ""}
                                                renderInput={(
                                                    params: JSX.IntrinsicAttributes & TextFieldProps
                                                ) => (
                                                    <TextField fullWidth size="small"                                                        
                                                        {...params}
                                                        inputProps={{
                                                            ...params.inputProps,
                                                            placeholder: lng === "ja" 
                                                            ? dayjs("2000-01-01").format("YYYY/MM/DD") 
                                                            : dayjs("2000-01-01").format("MM/DD/YYYY")
                                                        }}
                                                        label={t("KaguWorkForm.Birthday")}
                                                        InputLabelProps={{ shrink: true }}
                                                        error={errors.birthday ? true : false}
                                                        helperText={
                                                            <>
                                                                {ErrorIndicator(errors.birthday)}
                                                                {errors.birthday ? <br/> : ""}
                                                                {t("KaguWorkForm.BirthdayAnnotation",
                                                                    {date:lng === "ja" ? dayjs().locale(lng).format("YYYY/MM/DD") : dayjs().locale(lng).format("MMMM D, YYYY")})}
                                                            </>                                                            
                                                        }
                                                        FormHelperTextProps={{error:true}}
                                                    />
                                                )}
                                            />
                                        </LocalizationProvider>
                                    )}
                                />
                            </Box>

                            {/* 教育機関 */}
                            <Box pb={2}>
                                <Controller
                                    name="educational"
                                    control={control}
                                    defaultValue={""}
                                    rules={{required:true}}
                                    render={({field:{ref, ...field}, formState:{errors}}) => (
                                        <TextField
                                            inputRef={ref}
                                            {...field}
                                            label={t("KaguWorkForm.Educational")}
                                            variant="outlined" 
                                            fullWidth
                                            size="small"
                                            error={errors.educational ? true : false}
                                            helperText={ErrorIndicator(errors.educational)}
                                        />
                                    )}
                                />
                            </Box>

                            {/* 国・地域(教育機関) */}
                            <Box pb={2}>
                                <Controller
                                    name="educationalCountry"
                                    control={control}
                                    rules={{
                                        validate: {required: (value) => {return value !== 0 }}
                                    }}
                                    defaultValue={0}
                                    render={({ field:{ref, ...field}, formState: { errors } }) => (
                                        <FormControl fullWidth size="small"
                                            error={errors.educationalCountry ? true : false}>
                                            <InputLabel id="educationalCountry-label"
                                                error={errors.educationalCountry ? true : false}
                                            >
                                                {t("KaguWorkForm.EducationalCountry")}
                                            </InputLabel>
                                            <Select
                                                labelId="educationalCountry-label"
                                                id="educationalCountry"
                                                label={t("KaguWorkForm.EducationalCountry")}
                                                inputRef={ref}
                                                {...field}
                                                sx={{textAlign:'left'}}
                                            >                
                                                <MenuItem value={0}>{t("KaguWorkForm.CountryAnnotation")}</MenuItem>                                
                                                {CountryOption()}
                                            </Select>
                                            <FormHelperText error={errors.educationalCountry ? true : false}>
                                                {ErrorIndicator(errors.educationalCountry)}
                                            </FormHelperText>
                                        </FormControl>
                                    )}
                                />
                            </Box>

                            {/* 在籍証明ファイル */}
                            <Box>
                                <Controller
                                    name="certificate"
                                    control={control}
                                    rules={{
                                        required:true,
                                        validate: {
                                            fileSize: files => isFileSizeLessThan3MB(files),
                                            fileType: files => isCorrectFileType(files, ["application/pdf", "image/jpeg"])
                                        }
                                    }}
                                    render={({field:{ref, ...field}, formState:{errors}}) => (
                                        <MuiFileInput fullWidth size="small"
                                            inputRef={ref}
                                            {...field}
                                            label={t("KaguWorkForm.MainCertificate")}
                                            placeholder={t("Common.Select").toString()}
                                            error={errors.certificate ? true : false}
                                            helperText={
                                                <>
                                                    {(ErrorIndicator(errors.certificate))}
                                                    {errors.certificate ? <br/> : ""}
                                                    {t("KaguWorkForm.CertificateAnnotation1")}<br/>
                                                    {t("KaguWorkForm.CertificateAnnotation2")}<br/>
                                                    {t("KaguWorkForm.CertificateAnnotation3")}
                                                </>
                                            }
                                            inputProps={{accept: ".pdf, .jpg, .jpeg"}}
                                        />
                                    )}
                                />
                            </Box>                          
                        </Box>

                        {/* グループ情報入力欄 */}
                        <Box>
                            <Typography
                                variant='h6'
                                sx={{
                                    textAlign:"left",
                                    mt:1
                                }}
                            >
                                {t("KaguWorkForm.GroupAdditional")}
                            </Typography>  
                        </Box>

                        {/* グループチェックボックス */}
                        <Box flexDirection="row" justifyContent="flex-start" display="flex">
                            <Controller
                                name="group"
                                control={control}        
                                defaultValue={formData?.group}                        
                                render={({field:{value, ...field}}) => (
                                    <FormControlLabel
                                        control={
                                            <Checkbox size="small" 
                                                checked={value}
                                                {...field}
                                            />
                                        } 
                                        label={t("KaguWorkForm.GroupAnnotation")} 
                                    />
                                )}
                            />
                        </Box>
                        
                        {/* グループ情報 */}
                        {watchGroup ?
                            <Box>
                                <Info>
                                    {t("KaguWorkForm.GroupAlert1")}<br/>
                                    {t("KaguWorkForm.GroupAlert2")}
                                </Info>
                                
                                {fields.map((field, index) => (
                                    <Box key={field.id} mt={1} p={1} sx={{border:"1px solid",  borderColor: 'primary.main'}}>
                                        <Grid2 container spacing={1} alignItems="center">
                                            <Grid2>
                                                {t("KaguWorkForm.Member")}{index + 1}
                                            </Grid2>
                                            {index > 0 ?
                                                <Grid2>
                                                    <Button variant="outlined" color="warning" onClick={() => remove(index)}>
                                                        {t("KaguWorkForm.Delete")}
                                                    </Button>
                                                </Grid2>
                                            :""}
                                        </Grid2>

                                        {/* 氏名 */}
                                        <Box pb={1}>
                                            <Controller
                                                name={`members.${index}.name`}
                                                control={control}
                                                rules={{required:true}}
                                                render={({field:{ref, ...field}, formState:{errors}}) => (
                                                    <TextField
                                                        inputRef={ref}
                                                        {...field}
                                                        label={t("KaguWorkForm.Name")}
                                                        variant="outlined" 
                                                        fullWidth
                                                        size="small"
                                                        margin="normal"
                                                        error={errors.members?.[index]?.name ? true : false}
                                                        helperText={ErrorIndicator(errors.members?.[index]?.name)}
                                                    />
                                                )}
                                            />
                                        </Box>

                                        {/* 国・地域 */}
                                        <Box pb={2}>
                                            <Controller
                                                name={`members.${index}.country`}
                                                control={control}
                                                rules={{
                                                    validate: {required: (value) => {return value !== 0 }}
                                                }}
                                                render={({ field:{ref, ...field}, formState: { errors } }) => (
                                                    <FormControl fullWidth size="small"
                                                        error={errors.members?.[index]?.country ? true : false}>
                                                        <InputLabel id={`member-${index}-country-label`}
                                                            error={errors.members?.[index]?.country ? true : false}
                                                        >
                                                            {t("KaguWorkForm.Country")}
                                                        </InputLabel>
                                                        <Select
                                                            labelId={`member-${index}-country-label`}
                                                            label={t("KaguWorkForm.Country")}
                                                            inputRef={ref}
                                                            {...field}
                                                            sx={{textAlign:'left'}}                                                
                                                        >
                                                            <MenuItem value={0}>{t("KaguWorkForm.CountryAnnotation")}</MenuItem>
                                                            {CountryOption()}
                                                        </Select>
                                                        <FormHelperText error={errors.members?.[index]?.country ? true : false}>
                                                            {ErrorIndicator(errors.members?.[index]?.country)}
                                                        </FormHelperText>
                                                    </FormControl>
                                                )}
                                            />
                                        </Box>

                                        {/* 生年月日 */}
                                        <Box pb={1}>
                                            <Controller
                                                name={`members.${index}.birthday`}
                                                control={control}
                                                rules={{
                                                    required:true,
                                                    validate:{
                                                        invalidDate: (value) => {return dayjs(value, "YYYY/MM/DD", true).isValid()},
                                                        ageOver30: (value) => {return dayjs().diff(dayjs(value), "year") <= 30},                                                        
                                                    }
                                                    
                                                }}
                                                render={({field:{ref, ...field},formState:{errors}}) => (
                                                    <LocalizationProvider
                                                        dateAdapter={AdapterDayjs}
                                                        adapterLocale={lng}
                                                    >
                                                        <DesktopDatePicker
                                                            inputRef={ref}
                                                            {...field}
                                                            inputFormat={lng === "ja" ? "YYYY/MM/DD" : "MM/DD/YYYY"}
                                                            maxDate={dayjs().format("YYYY/MM/DD")}
                                                            value={field.value || ""}
                                                            renderInput={(
                                                                params: JSX.IntrinsicAttributes & TextFieldProps
                                                            ) => (
                                                                <TextField fullWidth size="small"                                                        
                                                                    {...params}
                                                                    inputProps={{
                                                                        ...params.inputProps,
                                                                        placeholder: lng === "ja" 
                                                                        ? dayjs("2000-01-01").format("YYYY/MM/DD") 
                                                                        : dayjs("2000-01-01").format("MM/DD/YYYY")
                                                                    }}
                                                                    label={t("KaguWorkForm.Birthday")}
                                                                    InputLabelProps={{ shrink: true }}
                                                                    error={errors.members?.[index]?.birthday ? true : false}
                                                                    helperText={
                                                                        <>
                                                                            {ErrorIndicator(errors.members?.[index]?.birthday)}
                                                                            {errors.members?.[index]?.birthday ? <br/> : ""}
                                                                            {t("KaguWorkForm.BirthdayAnnotation",
                                                                                {date:lng === "ja" ? dayjs().locale(lng).format("YYYY/MM/DD") : dayjs().locale(lng).format("MMMM D, YYYY")})}
                                                                        </>
                                                                    }
                                                                    FormHelperTextProps={{error:true}}
                                                                />
                                                            )}
                                                        />
                                                    </LocalizationProvider>
                                                )}
                                            />
                                        </Box>

                                        {/* 教育機関 */}
                                        <Box pb={2}>
                                            <Controller
                                                name={`members.${index}.educational`}
                                                control={control}
                                                defaultValue={""}
                                                rules={{required:true}}
                                                render={({field:{ref, ...field}, formState:{errors}}) => (
                                                    <TextField
                                                        inputRef={ref}
                                                        {...field}
                                                        label={t("KaguWorkForm.Educational")}
                                                        variant="outlined" 
                                                        fullWidth
                                                        size="small"
                                                        error={errors.members?.[index]?.educational ? true : false}
                                                        helperText={ErrorIndicator(errors.members?.[index]?.educational)}
                                                    />
                                                )}
                                            />
                                        </Box>

                                        {/* 国・地域(教育機関) */}
                                        <Box pb={2}>
                                            <Controller
                                                name={`members.${index}.educationalCountry`}
                                                control={control}
                                                rules={{
                                                    validate: {required: (value) => {return value !== 0 }}
                                                }}
                                                defaultValue={0}
                                                render={({ field:{ref, ...field}, formState: { errors } }) => (
                                                    <FormControl fullWidth size="small"
                                                        error={errors.members?.[index]?.educationalCountry ? true : false}>
                                                        <InputLabel id={`member-${index}-educationalCountry-label`}
                                                            error={errors.members?.[index]?.educationalCountry ? true : false}
                                                        >
                                                            {t("KaguWorkForm.EducationalCountry")}
                                                        </InputLabel>
                                                        <Select
                                                            labelId={`member-${index}-educationalCountry-label`}
                                                            label={t("KaguWorkForm.EducationalCountry")}
                                                            inputRef={ref}
                                                            {...field}
                                                            sx={{textAlign:'left'}}
                                                        >                
                                                            <MenuItem value={0}>{t("KaguWorkForm.CountryAnnotation")}</MenuItem>                                
                                                            {CountryOption()}
                                                        </Select>
                                                        <FormHelperText error={errors.members?.[index]?.educationalCountry ? true : false}>
                                                            {ErrorIndicator(errors.members?.[index]?.educationalCountry)}
                                                        </FormHelperText>
                                                    </FormControl>
                                                )}
                                            />
                                        </Box>

                                        {/* 在籍証明ファイル */}
                                        <Box>
                                            <Controller
                                                name={`members.${index}.certificate`}
                                                control={control}
                                                rules={{
                                                    required:true,
                                                    validate: {
                                                        fileSize: files => isFileSizeLessThan3MB(files),
                                                        fileType: files => isCorrectFileType(files, ["application/pdf", "image/jpeg"])
                                                    }
                                                }}
                                                render={({field:{ref, ...field}, formState:{errors}}) => (
                                                    <MuiFileInput fullWidth size="small"
                                                        inputRef={ref}
                                                        {...field}
                                                        label={t("KaguWorkForm.MemberCertificate")}
                                                        placeholder={t("Common.Select").toString()}
                                                        error={errors.members?.[index]?.certificate ? true : false}
                                                        helperText={(ErrorIndicator(errors.members?.[index]?.certificate))}
                                                        inputProps={{accept: ".pdf, .jpg, .jpeg"}}
                                                    />
                                                )}
                                            />
                                        </Box>                   
                                    </Box>

                                ))}


                                {/* グループ員追加ボタン */}
                                {fields.length < 5 ?
                                    <Box pt={1} flexDirection="row" justifyContent="flex-start" display="flex">
                                        <Button variant="outlined" onClick={() => append(memberDefault)}>
                                            +
                                        </Button>
                                    </Box>   
                                    : ""
                                }     
                            </Box>
                            : ""
                        }

                        {/* 作品情報 */}
                        <Box pt={2}>
                            <Typography
                                variant='h6'
                                sx={{
                                    textAlign:"left",
                                    mt:1
                                }}
                            >
                                {t("KaguWorkForm.WorkFile")}
                            </Typography> 
                            {/* 作品ファイル */}
                            <Box pt={1} pb={1}>
                                <Controller
                                    name="workFile"
                                    control={control}
                                    rules={{
                                        required:true,
                                        validate: {
                                            fileSize: files => isFileSizeLessThan3MB(files),
                                            fileType: files => isCorrectFileType(files, ["application/pdf"])
                                        }
                                    }}
                                    render={({field:{ref, ...field}, formState:{errors}}) => (
                                        <MuiFileInput fullWidth size="small"
                                            inputRef={ref}
                                            {...field}
                                            // label={t("KaguWorkForm.WorkFile")}
                                            placeholder={t("Common.Select").toString()}
                                            error={errors.workFile ? true : false}
                                            helperText={
                                                <>
                                                    {(ErrorIndicator(errors.workFile))}
                                                    {errors.workFile ? <br/> : ""}
                                                    {t("KaguWorkForm.WorkFileAnnotation1")}<br/>
                                                    {t("KaguWorkForm.WorkFileAnnotation2")}                                                    
                                                </>
                                            }
                                            inputProps={{accept: ".pdf"}}
                                        />
                                    )}
                                />
                            </Box>  

                            {/* 作品製品化の興味 */}
                            <Box flexDirection="row" justifyContent="flex-start" display="flex">
                                <Controller
                                    name="product"
                                    control={control}    
                                    defaultValue={formData?.product}                            
                                    // rules={{required:true}}
                                    render={({field:{value, ...field}}) => (
                                        <FormControlLabel
                                            control={
                                                <Checkbox size="small" 
                                                    checked={value}
                                                    {...field}
                                                />
                                            } 
                                            // label={t("KaguWorkForm.Product")} 
                                            label={<Trans i18nKey={"KaguWorkForm.Product"}/>}
                                            sx={{
                                                textAlign:'left'
                                            }}
                                        />
                                    )}
                                />
                            </Box>
                        </Box>
                        

                        <Grid2 container spacing={1} mt={1}>
                            <Grid2>
                                <Button variant="contained" type="submit">{t("KaguWorkForm.Confirm")}</Button>
                            </Grid2>
                        </Grid2>
                    </form>
                </Box>
            </Container>
        </> 
    );
}